import { ROUTE_LOGIN_OUT } from "@/constants/routes.constants";
import { useIndexedDBTamperDetection } from "@/hooks/useIndexDBTamperDetection";
import { Button } from "@/shadcn/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card";
import { refreshPinTimestamp, setPin, validatePin } from "@/utils/pin.util";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils.util";
import { t } from "i18next";
import localforage from "localforage";
import React, { useEffect, useState } from "react";
import PatternLock from "react-pattern-lock";
import { useNavigate } from "react-router";
import "./setpattern.css";

type SetPatternProps = {
  onPinValidated: (isValid: boolean) => void;
  onMaxAttemptsReached: () => void;
};

const SetPattern: React.FC<SetPatternProps> = ({
  onPinValidated,
  onMaxAttemptsReached,
}) => {
  const nav = useNavigate();

  useIndexedDBTamperDetection({
    dbName: "localforage",
    storeName: "keyvaluepairs",
    keysToMonitor: ["attempts", "user_pin", "pin_timestamp"],
    pollInterval: 750,
    onTamperDetected: async () => {
      nav(ROUTE_LOGIN_OUT);
    },
  });

  const [path, setPath] = useState<number[]>([]);
  const [attempts, setAttempts] = useState(0);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const channel = React.useMemo(() => new BroadcastChannel("pin_channel"), []);

  // Check localforage for attempts on component mount and on broadcast channel message
  useEffect(() => {
    channel.onmessage = (event) => {
      if (event.data === "attempt_made") {
        syncAttemptsWithLocalForage();
      }
    };

    async function syncAttemptsWithLocalForage() {
      const storedAttempts = await localforage.getItem<number>("attempts");
      if (storedAttempts !== null && storedAttempts !== attempts) {
        setAttempts(storedAttempts);
      }
    }
    if (attempts >= 3) {
      onMaxAttemptsReached();
    }
    syncAttemptsWithLocalForage();
  }, [attempts, channel, onMaxAttemptsReached]);

  const updateAttempts = async (newAttempts: number) => {
    setAttempts(newAttempts);
    channel.postMessage("attempt_made");
    await localforage.setItem("attempts", newAttempts);
  };

  // Check if the user is setting a new pattern or validating an existing one
  useEffect(() => {
    async function checkIfFirstTime() {
      const storedPin = await localforage.getItem<string | null>("user_pin");
      if (storedPin) {
        setIsFirstTime(false);
      } else {
        setIsFirstTime(true);
      }
    }
    checkIfFirstTime();
  }, [onPinValidated]);

  const handlePatternFinish = async () => {
    if (path.length === 1) {
      setPath([]);
      return;
    }
    const pin = path.join("");

    // Check if the user is setting a new pattern or validating an existing one
    if (isFirstTime) {
      if (pin.length < 4) {
        genericFailureToast(t("Pattern must be at least 4 points long."));
        setPath([]);
        return;
      }
      await setPin(pin);
      await updateAttempts(0);
      channel.postMessage("pin_set");
      genericSuccessToast(t("Pattern set successfully."), { duration: 2000 });
      onPinValidated(true);
    } else {
      // Validate the existing pattern
      const isValid = await validatePin(pin);
      if (isValid) {
        onPinValidated(true);
        await updateAttempts(0);
        await refreshPinTimestamp();
        channel.postMessage("pin_set");
      } else {
        const newAttempts = attempts + 1;
        await updateAttempts(newAttempts);
        genericFailureToast(t("Incorrect pattern. Please try again."));
      }
    }
    setPath([]);
  };

  return (
    <div className="bg-opacity-50 bg-card fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed top-1/2 left-1/2 flex h-full -translate-x-1/2 -translate-y-1/2 items-center justify-center p-5">
        <Card className="flex h-full justify-between border-0 shadow-none">
          <CardHeader>
            <CardTitle className="text-center text-2xl">
              {t(isFirstTime ? "Set Authentication" : "Enter Authentication")}
            </CardTitle>
            <CardDescription className="text-center">
              {isFirstTime
                ? t("Please set your pattern for this session.")
                : t("Please enter your pattern to unlock.")}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col justify-between">
            <div className="grow">
              <PatternLock
                width={260}
                pointSize={30}
                size={3}
                path={path}
                onChange={(pattern) => {
                  setPath(pattern);
                }}
                className={"rounded-2xl"}
                onFinish={handlePatternFinish}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col items-center justify-center text-sm">
            {isFirstTime
              ? t("The pattern must be at least 4 points long")
              : t(`You have ${3 - attempts} attempts remaining`)}
            <Button
              variant={"link"}
              className="p-1 font-bold text-blue-400"
              onClick={() => nav(ROUTE_LOGIN_OUT)}
            >
              {t("Logout")}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default SetPattern;
